/**
 * PURPOSE:
 * Mostrar en el link público la llave eliminatoria guardada del evento 17.
 *
 * RESPONSIBILITIES:
 * - Consultar la llave existente en modo solo lectura.
 * - Mostrar una vista compacta, moderna y conectada por rondas.
 * - Publicar la estructura desde que fue creada, sin exigir published = 1.
 * - Mostrar marcadores únicamente cuando el partido esté oficializado.
 * - Ocultar acciones y datos administrativos del módulo interno WINSCORE.
 *
 * COLLABORATORS:
 * - event17PublicBracket.api.js
 * - event17PublicBracket.mapper.js
 * - Event17PublicBracketMatch.jsx
 * - Event17PublicBracketConnectors.jsx
 * - src/utils/publicSportTabs.jsx
 *
 * ROUTE:
 * src/components/Fixture/components/Event17PublicBracket.jsx
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../../config/config.js";
import { getEvent17PublicBracket } from "./event17PublicBracket.api";
import { mapEvent17PublicBracket } from "./event17PublicBracket.mapper";
import Event17PublicBracketConnectors from "./Event17PublicBracketConnectors";
import Event17PublicBracketMatch from "./Event17PublicBracketMatch";

function LoadingState() {
  return (
    <section className="w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="animate-pulse space-y-4">
        <div className="mx-auto h-6 w-48 rounded bg-slate-200" />
        <div className="h-[360px] rounded-xl bg-slate-100" />
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="w-full rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
      <h3 className="text-lg font-bold text-slate-700">
        La llave aún no está disponible.
      </h3>
    </section>
  );
}

export default function Event17PublicBracket({ idevent, idsport }) {
  const canvasRef = useRef(null);
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // FTR-0006: moodName decide abrev (1) o displayName || name (2). Se inicia en 2
  // para conservar el render actual (nombre completo) mientras carga la configuración.
  const [moodName, setMoodName] = useState(2);

  useEffect(() => {
    let active = true;

    const loadConfig = async () => {
      try {
        const { data } = await axios.post(`${API_BASE_URL}/config_category`, {
          idevent: Number(idevent),
          idsport: Number(idsport),
        });
        if (active && data?.moodName != null) setMoodName(data.moodName);
      } catch (configError) {
        // Sin configuración se mantiene el comportamiento actual (nombre completo).
        console.error("Error cargando config_category para la llave pública:", configError);
      }
    };

    if (idevent && idsport) loadConfig();

    return () => {
      active = false;
    };
  }, [idevent, idsport]);

  const loadBracket = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const rawData = await getEvent17PublicBracket({ idevent, idsport });
      setBracket(mapEvent17PublicBracket(rawData));
    } catch (requestError) {
      console.error("Error cargando la llave pública del evento 17:", requestError);
      setError("No se pudo cargar la llave.");
    } finally {
      setLoading(false);
    }
  }, [idevent, idsport]);

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      setLoading(true);
      setError("");

      try {
        const rawData = await getEvent17PublicBracket({ idevent, idsport });
        if (active) setBracket(mapEvent17PublicBracket(rawData));
      } catch (requestError) {
        console.error(
          "Error cargando la llave pública del evento 17:",
          requestError
        );
        if (active) setError("No se pudo cargar la llave.");
      } finally {
        if (active) setLoading(false);
      }
    };

    bootstrap();

    return () => {
      active = false;
    };
  }, [idevent, idsport]);

  if (loading) return <LoadingState />;

  if (error) {
    return (
      <section className="w-full rounded-2xl border border-red-200 bg-red-50 px-6 py-10 text-center">
        <p className="font-semibold text-red-700">{error}</p>
        <button
          type="button"
          onClick={loadBracket}
          className="mt-4 inline-flex h-9 items-center justify-center rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-700 transition hover:bg-red-100"
        >
          Reintentar
        </button>
      </section>
    );
  }

  if (!bracket?.hasBracket || bracket.rounds.length === 0) {
    return <EmptyState />;
  }

  const rounds = bracket.rounds;
  const firstRoundMatches = rounds[0]?.matches?.length || 1;
  const cardWidth = 232;
  const columnGap = 72;
  const canvasHeight = Math.max(500, firstRoundMatches * 118 + 96);
  const canvasWidth = Math.max(
    300,
    rounds.length * cardWidth + (rounds.length - 1) * columnGap
  );

  return (
    <section className="w-full space-y-4 py-2 font-quickSand">
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-center shadow-sm">
        <h2 className="text-xl font-extrabold text-slate-900 sm:text-2xl">
          Llave de eliminación
        </h2>
      </div>

      <div className="w-full overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
        <div
          ref={canvasRef}
          className="relative grid"
          style={{
            width: canvasWidth,
            height: canvasHeight,
            gridTemplateColumns: `repeat(${rounds.length}, ${cardWidth}px)`,
            columnGap,
          }}
        >
          <Event17PublicBracketConnectors
            containerRef={canvasRef}
            rounds={rounds}
          />

          {rounds.map((round, roundIndex) => (
            <div
              key={round.key || round.number}
              className="relative z-10 flex h-full flex-col"
            >
              <div
                className={`mb-2 rounded-lg px-3 py-2 text-center text-xs font-bold uppercase tracking-wide ${
                  roundIndex === 0
                    ? "bg-blue-600 text-white"
                    : "bg-slate-200 text-slate-700"
                }`}
              >
                {round.label}
              </div>

              <div className="flex min-h-0 flex-1 flex-col justify-around py-3">
                {(round.matches || []).map((match) => (
                  <Event17PublicBracketMatch
                    key={match.code}
                    match={match}
                    moodName={moodName}
                    emphasized={roundIndex === 0}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
